import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { isApiError } from "@/api/client";
import { api, call, xClient } from "@/api/spec";
import type { Product } from "@/api/types";
import { analytics } from "@/analytics";
import { isMockBilling } from "@/platform/billing";
import { t } from "@/i18n/ko";
import { productName } from "@/i18n/codes";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";

/** 결제창에서 돌아오는 경로. 토스는 쿼리로 paymentKey·orderId·amount(성공) / code·message·orderId(실패)를 붙인다 */
export const WEB_SUCCESS_PATH = "/store/web/success";
export const WEB_FAIL_PATH = "/store/web/fail";

/**
 * 모의 결제(결제창 없이 `mock:<orderId>` 로 바로 성공 경로): 로컬 빌드(VITE_BILLING=mock, AC0-302) 또는 dev 스테이지 빌드에서 어드민 웹 결제 모드가 mock(AC0-304).
 * prod 빌드는 항상 토스 결제창. 서버도 TOSS_VERIFY=mock ∧ 설정 mock 에서만 모의 키를 받는다
 */
export const isMockWebCheckout = () => isMockBilling(import.meta.env) || (import.meta.env.VITE_STAGE === "dev" && useConfigStore.getState().settings?.webBilling === "mock");

/** 토스 사용자 취소 코드(결제창 SDK 오류·failUrl 공통). 취소는 조용히 둔다 */
const TOSS_CANCEL_CODES = new Set(["PAY_PROCESS_CANCELED", "USER_CANCEL"]);
export const isTossCancel = (code: string | null | undefined): boolean => !!code && TOSS_CANCEL_CODES.has(code);

const tossCode = (e: unknown): string | null =>
  !isApiError(e) && typeof e === "object" && e !== null && typeof (e as { code?: unknown }).code === "string" ? (e as { code: string }).code : null;

/** 결제창 단계 실패(청구 전, 취소 제외) → F06-03 다이얼로그(StorePage). 서버 API 오류·설정 오류는 제외 */
export const isTossFailure = (e: unknown): boolean => {
  const code = tossCode(e);
  return code !== null && !isTossCancel(code);
};

/** failUrl 로 돌아왔을 때 [다시 시도] 할 상품. 결제창으로 떠나기 전에 적어 두고, 돌아오면 한 번 꺼낸다(세션 저장소, 실패해도 무시) */
const CHECKOUT_PRODUCT_KEY = "hamlak.webCheckoutProduct";
function rememberCheckoutProduct(productId: string): void {
  try {
    sessionStorage.setItem(CHECKOUT_PRODUCT_KEY, productId);
  } catch {
    // 저장소를 못 쓰면 다시 시도는 다이얼로그만 닫는다
  }
}
export function takeCheckoutProduct(): string | null {
  try {
    const id = sessionStorage.getItem(CHECKOUT_PRODUCT_KEY);
    sessionStorage.removeItem(CHECKOUT_PRODUCT_KEY);
    return id;
  } catch {
    return null;
  }
}

export type WebCheckoutStart = { kind: "mock"; successPath: string } | { kind: "redirect" } | { kind: "canceled" } | { kind: "unavailable" };

/**
 * 웹 결제 시작 (상점·결제 설계 결정 10). 1) POST /purchases/web/orders 로 서버가 금액을 확정 → 2) 토스페이먼츠 결제창형(AC0-309, 리다이렉트) →
 * 3) successUrl(WebSuccessPage)에서 POST /purchases/web/confirm. 클라는 금액을 정하지 않는다 — 주문 응답의 amount 를 그대로 결제창에 넘긴다.
 * 결제창형: widgets.setAmount → renderPaymentWindow(구매자가 결제수단 선택) → paymentRequest 이벤트에서 requestPayment. 결제창을 닫으면(cancel) canceled.
 * customerKey 가 없으면 비회원(ANONYMOUS). 키 세트는 결제창형 연동 키(test_gck/test_gsk) — 결제창(구버전) 키(ck/sk)와 섞으면 승인이 거절된다.
 * 토스 클라이언트 키가 없는 빌드(PRD 연동 전, AC0-313)는 주문을 만들지 않고 unavailable — 상점이 "결제 준비 중" 토스트
 */
export async function startWebCheckout(product: Product, customerKey: string | null): Promise<WebCheckoutStart> {
  const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY;
  if (!isMockWebCheckout() && !clientKey) return { kind: "unavailable" };
  const order = await call(api.POST("/purchases/web/orders", { params: { header: xClient() }, body: { productId: product.productId } }));
  const origin = window.location.origin;
  const successUrl = `${origin}${WEB_SUCCESS_PATH}`;
  const failUrl = `${origin}${WEB_FAIL_PATH}`;
  if (isMockWebCheckout()) {
    const q = new URLSearchParams({ paymentKey: `mock:${order.orderId}`, orderId: order.orderId, amount: String(order.amount) });
    return { kind: "mock", successPath: `${WEB_SUCCESS_PATH}?${q.toString()}` };
  }
  if (!clientKey) throw new Error("VITE_TOSS_CLIENT_KEY is not set");
  const { loadTossPayments, ANONYMOUS } = await import("@tosspayments/tosspayments-sdk");
  const toss = await loadTossPayments(clientKey);
  const widgets = toss.widgets({ customerKey: customerKey ?? ANONYMOUS });
  await widgets.setAmount({ currency: order.currency as "KRW", value: order.amount });
  // renderPaymentWindow 와 requestPayment 의 orderName 이 다르면 SDK 가 InvalidParametersError
  const orderName = productName({ bricks: order.bricks });
  const paymentWindow = await widgets.renderPaymentWindow({ orderName });
  rememberCheckoutProduct(product.productId);
  return new Promise<WebCheckoutStart>((resolve, reject) => {
    paymentWindow.on("paymentRequest", async () => {
      try {
        await widgets.requestPayment({ orderId: order.orderId, orderName, successUrl, failUrl });
        resolve({ kind: "redirect" });
      } catch (e) {
        // 결제창을 걷고 끝낸다(취소는 조용히, 그 밖은 오류). 다시 결제하면 새 주문으로 시작한다
        void paymentWindow.destroy().catch(() => undefined);
        if (isTossCancel(tossCode(e))) resolve({ kind: "canceled" });
        else reject(e);
      }
    });
    paymentWindow.on("cancel", async () => resolve({ kind: "canceled" }));
  });
}

/**
 * 상점 버튼 → 웹 결제. 모의면 성공 경로로 바로 이동, 실제면 결제창이 페이지를 떠난다. 연동 전 빌드는 "결제 준비 중" 토스트.
 * 결제창 취소는 조용히, 결제창 실패는 StorePage 다이얼로그(isTossFailure), 그 밖의 실패는 토스트(REGION_UNSUPPORTED 는 상점이 미리 막는다)
 */
export function useWebCheckout() {
  const navigate = useNavigate();
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  return useMutation({
    mutationFn: async (product: Product) => {
      analytics.track("start_purchase", { product_id: product.productId });
      const customerKey = useAuthStore.getState().user?.id ?? null;
      return startWebCheckout(product, customerKey);
    },
    onSuccess: (r) => {
      if (r.kind === "mock") navigate(r.successPath);
      else if (r.kind === "unavailable") showSnackbar(t("store.purchase.preparing"));
    },
    onError: (e) => {
      if (isTossCancel(tossCode(e)) || isTossFailure(e)) return;
      const code = isApiError(e) ? e.code : "UNKNOWN";
      if (code === "REGION_UNSUPPORTED") return showSnackbar(t("store.web.unsupported.title"));
      if (code === "PRODUCT_UNKNOWN") return showSnackbar(t("store.productUnknown"));
      showSnackbar(code === "NETWORK" ? t("error.network") : t("store.purchase.failed"));
    },
  });
}
